import { escapeHtml } from '@colplat/ui-web';
import { apiFetch } from '../api.js';

/**
 * What the bank actually said about an imported transaction — legacy's
 * `tx_bank_info` badge and its hover panel.
 *
 * A row that came from a bank carries a small marker; pointing at it (or
 * tabbing to it) fetches the feed's own record for that transaction and shows
 * it. **Fetched on demand, never with the list**: `bankRaw` is the provider's
 * whole payload for one entry, and a register of forty rows would otherwise
 * send hundreds of kilobytes that nobody reads. The list carries only which
 * rows have one.
 *
 * The badge is focusable and answers `focus` as well as `hover`, as legacy's
 * does — a detail worth keeping, since a marker you can only reach with a
 * mouse is a marker some people cannot reach at all. It also carries a
 * `title`, so the information is available without the panel; colour is never
 * the only signal (invariant #11) and neither is position.
 *
 * One cache for the page. The payload for a given transaction cannot change
 * without a re-sync, and re-fetching it every time the pointer crosses the
 * badge would turn a hover into a request storm.
 */

interface BankInfoSource {
  bank: string; country: string; account: string; iban: string;
  currency: string; reference: string; feed: string;
}

interface BankInfoEntry { source: BankInfoSource; raw: unknown }

const cache = new Map<string, BankInfoEntry[]>();

export function bankInfoBadgeHtml(transactionId: string): string {
  return `<span class="wl-bankbadge" data-bankinfo="${escapeHtml(transactionId)}" tabindex="0" role="button"
    aria-label="Imported from a bank — show what the bank sent"
    title="Imported from a bank — hover for the details">&#x1F3E6;</span>`;
}

/**
 * Flatten whatever shape the bank sent into label/value lines.
 *
 * Deliberately shape-agnostic. Enable Banking's entry payload differs between
 * banks and gains fields over time, and a renderer that knew the field names
 * would quietly drop whatever it had not been told about — which is the
 * opposite of what "show me what the bank sent" is for. Empty values are
 * skipped because a panel of blank rows hides the three lines that matter.
 */
export function bankInfoLines(
  value: unknown, path: string[] = [], out: Array<[string, string]> = [],
): Array<[string, string]> {
  if (value === null || value === undefined || value === '') return out;
  if (Array.isArray(value)) {
    if (value.length === 0) return out;
    // A list of plain values reads better as one line than as n numbered ones.
    if (value.every((v) => typeof v !== 'object' || v === null)) {
      out.push([fieldLabel(path), value.map((v) => String(v)).join(', ')]);
      return out;
    }
    value.forEach((v, i) => bankInfoLines(v, [...path, String(i + 1)], out));
    return out;
  }
  if (typeof value === 'object') {
    for (const [key, v] of Object.entries(value)) bankInfoLines(v, [...path, key], out);
    return out;
  }
  // Only JSON primitives become a line. A bank payload cannot contain a
  // symbol or a function, so anything else here did not come from the bank
  // and stringifying it would print machinery rather than data.
  if (typeof value === 'string' || typeof value === 'number' || typeof value === 'boolean') {
    out.push([fieldLabel(path), String(value)]);
  }
  return out;
}

function fieldLabel(path: string[]): string {
  const last = path[path.length - 1] ?? '';
  return last.replace(/_/g, ' ').replace(/^./, (c) => c.toUpperCase());
}

/** The panel's markup for a fetched answer. Pure, so its shape is testable without a DOM. */
export function bankInfoPanelHtml(entries: readonly BankInfoEntry[]): string {
  if (entries.length === 0) {
    return '<div class="wl-bi__title">No bank record for this transaction.</div>';
  }
  const row = (k: string, v: string): string =>
    `<div class="wl-bi__row"><span>${escapeHtml(k)}</span><span>${escapeHtml(v)}</span></div>`;

  // More than one entry is normal, not an oddity: a currency exchange is one
  // economic event the bank reports twice, and after a merge both feed
  // references live on the surviving transaction.
  const title = `<div class="wl-bi__title">As the bank sent it${
    entries.length > 1 ? ` · ${entries.length} entries` : ''}</div>`;

  return title + entries.map((entry, index) => {
    const s = entry.source;
    const head: Array<[string, string]> = [
      ['Bank', [s.bank, s.country].filter(Boolean).join(' · ')],
      ['Account', [s.account, s.iban, s.currency].filter(Boolean).join(' · ')],
      ['Feed reference', s.reference],
    ];
    const lines = bankInfoLines(entry.raw);
    return (index > 0 ? '<div class="wl-bi__sep"></div>' : '')
      + (entries.length > 1 ? `<div class="wl-bi__entry">Entry ${index + 1}</div>` : '')
      + head.filter(([, v]) => v).map(([k, v]) => row(k, v)).join('')
      + (lines.length > 0 ? '<div class="wl-bi__sep"></div>' : '')
      + lines.map(([k, v]) => row(k, v)).join('')
      // A bank connection that has since been disconnected keeps its feed key
      // and loses its bank name; an entry imported before payloads were kept
      // has no `raw` at all. Both are worth saying rather than showing a gap.
      + (entry.raw === null ? '<div class="wl-bi__note">Imported before the details were kept — '
        + 'a future sync of this account will fill them in.</div>' : '');
  }).join('');
}

/**
 * Wire the badges on a page. Delegated from the container, so rows re-rendered
 * after an edit need no re-wiring — the same reason the register delegates its
 * own row handlers.
 */
export interface BankInfoOptions {
  /**
   * This transaction's bank entries from the local replica (ADR-0025).
   *
   * Answered without a request when it can be, which is what makes the panel
   * open offline — and instantly online. The rows are on the device; only the
   * consent internals stayed behind.
   */
  localEntries?: (transactionId: string) => BankInfoEntry[];
}

export function mountBankInfo(container: HTMLElement, token: string, opts: BankInfoOptions = {}): void {
  const panel = document.createElement('div');
  panel.className = 'wl-bi';
  panel.hidden = true;
  document.body.appendChild(panel);

  let showingFor = '';

  const hide = (): void => { showingFor = ''; panel.hidden = true; };

  const show = (badge: HTMLElement): void => {
    const id = badge.dataset['bankinfo'];
    if (!id) return;
    showingFor = id;

    const cached = cache.get(id);
    if (cached) { paint(badge, bankInfoPanelHtml(cached)); return; }

    const local = opts.localEntries?.(id);
    if (local && local.length > 0) {
      cache.set(id, local);
      paint(badge, bankInfoPanelHtml(local));
      return;
    }

    paint(badge, '<div class="wl-bi__title">Loading…</div>');
    void (async () => {
      const res = await apiFetch(`/apps/webledger/transactions/${id}/bank-info`, { token })
        .catch(() => undefined);
      if (!res?.ok) {
        // A failed lookup hides the panel rather than parking an error over
        // the row: nothing was lost, and the badge is still there to try again.
        if (showingFor === id) hide();
        return;
      }
      const body = await res.json() as { entries: BankInfoEntry[] };
      cache.set(id, body.entries);
      // The pointer may have moved on while this was in flight; painting then
      // would drop a panel next to a row nobody is looking at.
      if (showingFor === id) paint(badge, bankInfoPanelHtml(body.entries));
    })();
  };

  const paint = (badge: HTMLElement, html: string): void => {
    panel.innerHTML = html;
    panel.hidden = false;
    place(badge, panel);
  };

  container.addEventListener('mouseover', (event) => {
    const badge = (event.target as HTMLElement).closest<HTMLElement>('[data-bankinfo]');
    if (badge) show(badge);
  });
  container.addEventListener('mouseout', (event) => {
    if ((event.target as HTMLElement).closest('[data-bankinfo]')) hide();
  });
  container.addEventListener('focusin', (event) => {
    const badge = (event.target as HTMLElement).closest<HTMLElement>('[data-bankinfo]');
    if (badge) show(badge);
  });
  container.addEventListener('focusout', (event) => {
    if ((event.target as HTMLElement).closest('[data-bankinfo]')) hide();
  });
  // Escape closes it, because a panel opened by keyboard has to be closable by
  // keyboard without tabbing away from the row you are on.
  container.addEventListener('keydown', (event) => {
    if (event.key === 'Escape' && !panel.hidden) { hide(); }
  });
}

/** Keep the panel on screen: below the badge if it fits, nudged inside the viewport if not. */
function place(badge: HTMLElement, panel: HTMLElement): void {
  const rect = badge.getBoundingClientRect();
  const top = Math.max(8, Math.min(rect.bottom + 6, window.innerHeight - panel.offsetHeight - 8));
  const left = Math.max(8, Math.min(rect.left, window.innerWidth - panel.offsetWidth - 8));
  panel.style.top = `${top}px`;
  panel.style.left = `${left}px`;
}
